import cn from "classnames"
import { useSearchParams } from "react-router-dom"

import { useGetAdrState } from "@/api/useIncidents"
import { Tabs } from "@/atoms"
import { TAB_KEYS_BY_PAGE } from "@/const/tab.constants"
import { isLearningState, isPrerequisitesNotMetState } from "@/domain/adr"
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { HeaderPage } from "@/organisms/HeaderPage"
import { useEnvironmentsForAdrIncident } from "@/pages/TriagePage/application/useAdrIncidents"
import { TAB_KEY } from "@/pages/TriagePage/const/filters.constants"
import { PageTemplate } from "@/templates/PageTemplate"
import { TabsTemplate } from "@/templates/TabsTemplate"
import { DetailsSection } from "./components/organisms/DetailsSection"
import { NoIncidentsBackground } from "./components/organisms/NoIncidentsBackground"
import { TabAdrManagement } from "./components/organisms/TabAdrManagement"

import styles from "./AdrManagementPage.module.scss"

export const AdrManagementPage = () => {
  const { INCIDENTS: keys } = TAB_KEYS_BY_PAGE
  const [searchParams, setSearchParams] = useSearchParams()
  const activeKey = searchParams.get(TAB_KEY) || keys[0]
  const adrIncidentsEnabled = useFeatureFlags("adrIncidentsEnabled")
  const config = { enabled: !!adrIncidentsEnabled }
  const { response, isLoading } = useEnvironmentsForAdrIncident(config)
  const { response: adrStateResponse, isLoading: isAdrStateLoading } =
    useGetAdrState(config)
  const { logger, EVENTS } = useLogger()
  const onChange = (key: (typeof keys)[number]) => {
    logger.info(EVENTS.ANALYTIC_EVENTS.ADR.OPEN_DISMISSED_TAB)

    setSearchParams(() => new URLSearchParams({ [TAB_KEY]: key }))
  }
  const loading = isLoading || isAdrStateLoading

  const { t } = useTranslation()
  const { hasIncidents } = response || {}
  const isSuccess = !loading && hasIncidents === false

  const tabsTemplateId = "tabs-template-adr-management-page"
  const sentinelId = `${tabsTemplateId}-sentinel`

  useIntersectionObserver(
    `#${sentinelId}`,
    ([entry]) => {
      document
        .getElementById(tabsTemplateId)
        ?.classList.toggle(styles.tabsTemplateIsStuck, !entry.isIntersecting)
    },
    { threshold: 0 }
  )

  const adrStateValue = adrStateResponse?.data?.state
  const showTeaser =
    isPrerequisitesNotMetState(adrStateValue) || isLearningState(adrStateValue)

  return (
    <PageTemplate
      full
      header={<HeaderPage titleTranslationKey="incidents" />}
      classes={{
        main: cn(styles.pageTemplate, {
          [styles.pageTemplateTeaser]: loading || showTeaser
        })
      }}
    >
      <div className={styles.wrapper}>
        <div
          className={cn(styles.wrapperBg, {
            [styles.wrapperIncident]: hasIncidents === true
          })}
        >
          {isSuccess && <NoIncidentsBackground />}
        </div>
        <div className={styles.section}>
          <DetailsSection config={config} />
        </div>
      </div>
      <div id={sentinelId} />
      <TabsTemplate
        id={tabsTemplateId}
        className={styles.tabsTemplate}
        data-tab-adr-management="true"
      >
        <Tabs
          destroyOnHidden
          activeKey={activeKey}
          items={keys.map((key) => ({
            key,
            label: t(`general.${key === keys[0] ? "incident_other" : key}`),
            children: (
              <TabAdrManagement
                showTeaser={showTeaser}
                parentId={tabsTemplateId}
                column={key === keys[0]}
                config={config}
                isActive={activeKey === key}
              />
            )
          }))}
          onChange={onChange}
        />
      </TabsTemplate>
    </PageTemplate>
  )
}
